/**
 * Training Dashboard Page
 * GitHub Issue #285
 *
 * Main admin view for managing compliance training modules and campaigns.
 */

import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  api,
  TrainingCategory,
  TrainingModule,
  TrainingCampaign,
  TrainingDashboardStats,
  TrainingContentType,
  TrainingModuleStatus,
  TrainingCampaignStatus,
  TrainingAssignmentType,
  TrainingScheduleType,
} from '../lib/api'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import { useAuth } from '../hooks/useAuth'
import {
  GraduationCap,
  Plus,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Edit2,
  Trash2,
  X,
  Play,
  Pause,
  CheckCircle2,
  Clock,
  Users,
  BookOpen,
  Video,
  FileText,
  HelpCircle,
  BarChart3,
  Folder,
  MoreVertical,
  Eye,
  Copy,
  Archive,
  Send,
  Calendar,
  AlertCircle,
  TrendingUp,
  Settings,
  Download,
  Sparkles,
  Award,
  Target,
  Building2,
  RefreshCw,
  Loader2,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts'

// Tab types
type TabType = 'dashboard' | 'modules' | 'campaigns' | 'categories'

// Dashboard sub-tabs for analytics
type DashboardSubTab = 'overview' | 'trends' | 'performers' | 'effectiveness' | 'departments'

// Types for advanced analytics
interface TrendData {
  month: string
  completions: number
  avg_score: number
}

interface TopPerformer {
  user_id: string
  username: string
  email: string
  first_name: string | null
  last_name: string | null
  modules_completed: number
  avg_score: number | null
  quizzes_passed: number
}

interface ModuleEffectiveness {
  module_id: string
  title: string
  content_type: TrainingContentType
  total_attempts: number
  completions: number
  completion_rate: number
  avg_score: number | null
  pass_rate: number | null
  avg_completion_minutes: number | null
}

interface DepartmentBreakdown {
  role: string
  total_assignments: number
  completed: number
  overdue: number
  completion_rate: number
  avg_days_to_complete: number | null
}

// Content type icons
const contentTypeIcons: Record<TrainingContentType, any> = {
  video: Video,
  slides: FileText,
  quiz: HelpCircle,
  document: FileText,
  mixed: BookOpen,
}

const contentTypeLabels: Record<TrainingContentType, string> = {
  video: 'Video',
  slides: 'Slides',
  quiz: 'Quiz',
  document: 'Document',
  mixed: 'Mixed',
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-yellow-100 text-yellow-700',
  active: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
}

const CHART_COLORS = ['#6366f1', '#22c55e', '#eab308', '#ef4444', '#8b5cf6']

export default function Training() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const confirmDialog = useConfirm()

  // State
  const [activeTab, setActiveTab] = useState<TabType>('dashboard')
  const [loading, setLoading] = useState(true)
  const [dashboardStats, setDashboardStats] = useState<TrainingDashboardStats | null>(null)
  const [modules, setModules] = useState<TrainingModule[]>([])
  const [campaigns, setCampaigns] = useState<TrainingCampaign[]>([])
  const [categories, setCategories] = useState<TrainingCategory[]>([])
  const [modulePagination, setModulePagination] = useState({ total: 0, limit: 20, offset: 0 })
  const [campaignPagination, setCampaignPagination] = useState({ total: 0, limit: 20, offset: 0 })

  // Filters
  const [moduleSearch, setModuleSearch] = useState('')
  const [moduleStatusFilter, setModuleStatusFilter] = useState<TrainingModuleStatus | ''>('')
  const [moduleCategoryFilter, setModuleCategoryFilter] = useState('')
  const [campaignSearch, setCampaignSearch] = useState('')
  const [campaignStatusFilter, setCampaignStatusFilter] = useState<TrainingCampaignStatus | ''>('')
  const [showFilters, setShowFilters] = useState(false)

  // Modals
  const [showCreateModuleModal, setShowCreateModuleModal] = useState(false)
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false)
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<TrainingCategory | null>(null)

  // Form data
  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    category_id: '',
    content_type: 'video' as TrainingContentType,
    video_url: '',
    estimated_minutes: 30,
    is_mandatory: false,
    quiz_passing_score: 70,
  })

  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    module_ids: [] as string[],
    assignment_type: 'all_users' as TrainingAssignmentType,
    schedule_type: 'immediate' as TrainingScheduleType,
    due_days_from_assignment: 14,
    send_reminders: true,
    reminder_days_before: [7, 3, 1],
  })

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: 'BookOpen',
    color: '#6366f1',
  })

  // Advanced Analytics State (Phase 4)
  const [dashboardSubTab, setDashboardSubTab] = useState<DashboardSubTab>('overview')
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [trends, setTrends] = useState<TrendData[]>([])
  const [topPerformers, setTopPerformers] = useState<TopPerformer[]>([])
  const [moduleEffectiveness, setModuleEffectiveness] = useState<ModuleEffectiveness[]>([])
  const [departmentBreakdown, setDepartmentBreakdown] = useState<DepartmentBreakdown[]>([])
  const [exportLoading, setExportLoading] = useState(false)

  // AI Generation State (Phase 4)
  const [aiGenerating, setAiGenerating] = useState(false)
  const [aiQuizTopic, setAiQuizTopic] = useState('')
  const [aiQuizDifficulty, setAiQuizDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium')
  const [aiNumQuestions, setAiNumQuestions] = useState(5)
  const [generatedQuestions, setGeneratedQuestions] = useState<any[]>([])
  const [showAIModal, setShowAIModal] = useState(false)

  // Fetch data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.getTrainingDashboard()
      if (response.success) {
        setDashboardStats(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error)
    }
  }, [])

  const fetchModules = useCallback(async () => {
    try {
      const params: any = { limit: 20, offset: modulePagination.offset }
      if (moduleSearch) params.search = moduleSearch
      if (moduleStatusFilter) params.status = moduleStatusFilter
      if (moduleCategoryFilter) params.category_id = moduleCategoryFilter

      const response = await api.getTrainingModules(params)
      if (response.success) {
        setModules(response.data)
        setModulePagination(response.pagination)
      }
    } catch (error) {
      console.error('Failed to fetch modules:', error)
    }
  }, [moduleSearch, moduleStatusFilter, moduleCategoryFilter, modulePagination.offset])

  const fetchCampaigns = useCallback(async () => {
    try {
      const params: any = { limit: 20, offset: campaignPagination.offset }
      if (campaignSearch) params.search = campaignSearch
      if (campaignStatusFilter) params.status = campaignStatusFilter

      const response = await api.getTrainingCampaigns(params)
      if (response.success) {
        setCampaigns(response.data)
        setCampaignPagination(response.pagination)
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error)
    }
  }, [campaignSearch, campaignStatusFilter, campaignPagination.offset])

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.getTrainingCategories({ include_inactive: true })
      if (response.success) {
        setCategories(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }, [])

  // Advanced Analytics Fetch Functions (Phase 4)
  const fetchTrends = useCallback(async () => {
    try {
      const response = await api.getTrainingTrends(6)
      if (response.success) {
        setTrends(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch trends:', error)
    }
  }, [])

  const fetchTopPerformers = useCallback(async () => {
    try {
      const response = await api.getTrainingTopPerformers(10)
      if (response.success) {
        setTopPerformers(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch top performers:', error)
    }
  }, [])

  const fetchModuleEffectiveness = useCallback(async () => {
    try {
      const response = await api.getTrainingModuleEffectiveness()
      if (response.success) {
        setModuleEffectiveness(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch module effectiveness:', error)
    }
  }, [])

  const fetchDepartmentBreakdown = useCallback(async () => {
    try {
      const response = await api.getTrainingByDepartment()
      if (response.success) {
        setDepartmentBreakdown(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch department breakdown:', error)
    }
  }, [])

  // Load analytics data based on sub-tab
  const loadAnalyticsData = useCallback(async (subTab: DashboardSubTab) => {
    if (subTab === 'overview') return // Already loaded
    setAnalyticsLoading(true)
    try {
      switch (subTab) {
        case 'trends':
          await fetchTrends()
          break
        case 'performers':
          await fetchTopPerformers()
          break
        case 'effectiveness':
          await fetchModuleEffectiveness()
          break
        case 'departments':
          await fetchDepartmentBreakdown()
          break
      }
    } finally {
      setAnalyticsLoading(false)
    }
  }, [fetchTrends, fetchTopPerformers, fetchModuleEffectiveness, fetchDepartmentBreakdown])

  // Export handler
  const handleExportReport = async (type: 'assignments' | 'progress') => {
    setExportLoading(true)
    try {
      const blob = await api.exportTrainingReport({ format: 'csv', type }) as Blob
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `training-${type}-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('Report exported successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to export report')
    } finally {
      setExportLoading(false)
    }
  }

  // AI Quiz Generation
  const handleGenerateQuiz = async () => {
    if (!aiQuizTopic.trim()) {
      toast.error('Please enter a topic')
      return
    }
    setAiGenerating(true)
    setGeneratedQuestions([])
    try {
      const response = await api.generateTrainingQuiz({
        topic: aiQuizTopic,
        num_questions: aiNumQuestions,
        difficulty: aiQuizDifficulty,
      })
      if (response.success) {
        setGeneratedQuestions(response.data.questions)
        toast.success(`Generated ${response.data.questions.length} questions`)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate quiz')
    } finally {
      setAiGenerating(false)
    }
  }

  // AI Description Generation
  const handleGenerateDescription = async () => {
    if (!moduleForm.title.trim()) {
      toast.error('Please enter a module title first')
      return
    }
    setAiGenerating(true)
    try {
      const response = await api.generateTrainingDescription({
        title: moduleForm.title,
        content_type: moduleForm.content_type,
      })
      if (response.success) {
        setModuleForm({ ...moduleForm, description: response.data.description })
        toast.success('Description generated')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate description')
    } finally {
      setAiGenerating(false)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchDashboard(), fetchCategories()])
      setLoading(false)
    }
    loadData()
  }, [fetchDashboard, fetchCategories])

  useEffect(() => {
    if (activeTab === 'modules') {
      fetchModules()
    } else if (activeTab === 'campaigns') {
      fetchCampaigns()
    }
  }, [activeTab, fetchModules, fetchCampaigns])

  // Load analytics when dashboard sub-tab changes
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadAnalyticsData(dashboardSubTab)
    }
  }, [activeTab, dashboardSubTab, loadAnalyticsData])

  // Module actions
  const handleCreateModule = async () => {
    try {
      const response = await api.createTrainingModule({
        title: moduleForm.title,
        description: moduleForm.description || undefined,
        category_id: moduleForm.category_id || undefined,
        content_type: moduleForm.content_type,
        video_url: moduleForm.video_url || undefined,
        estimated_minutes: moduleForm.estimated_minutes,
        is_mandatory: moduleForm.is_mandatory,
        quiz_passing_score: moduleForm.quiz_passing_score,
      })
      if (response.success) {
        toast.success('Module created successfully')
        setShowCreateModuleModal(false)
        resetModuleForm()
        fetchModules()
        // Navigate to edit the module
        navigate(`/training/modules/${response.data.id}`)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create module')
    }
  }

  const handlePublishModule = async (module: TrainingModule) => {
    try {
      const response = await api.publishTrainingModule(module.id)
      if (response.success) {
        toast.success('Module published')
        fetchModules()
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to publish module')
    }
  }

  const handleArchiveModule = async (module: TrainingModule) => {
    const confirmed = await confirmDialog({
      title: 'Archive Module',
      message: `Are you sure you want to archive "${module.title}"? It will no longer be available for new assignments.`,
      confirmText: 'Archive',
      type: 'warning',
    })
    if (confirmed) {
      try {
        const response = await api.archiveTrainingModule(module.id)
        if (response.success) {
          toast.success('Module archived')
          fetchModules()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to archive module')
      }
    }
  }

  const handleDeleteModule = async (module: TrainingModule) => {
    const confirmed = await confirmDialog({
      title: 'Delete Module',
      message: `Are you sure you want to delete "${module.title}"? This cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
    })
    if (confirmed) {
      try {
        const response = await api.deleteTrainingModule(module.id)
        if (response.success) {
          toast.success('Module deleted')
          fetchModules()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete module')
      }
    }
  }

  // Campaign actions
  const handleCreateCampaign = async () => {
    try {
      const response = await api.createTrainingCampaign({
        name: campaignForm.name,
        description: campaignForm.description || undefined,
        module_ids: campaignForm.module_ids,
        assignment_type: campaignForm.assignment_type,
        schedule_type: campaignForm.schedule_type,
        due_days_from_assignment: campaignForm.due_days_from_assignment,
        send_reminders: campaignForm.send_reminders,
        reminder_days_before: campaignForm.reminder_days_before,
      })
      if (response.success) {
        toast.success('Campaign created successfully')
        setShowCreateCampaignModal(false)
        resetCampaignForm()
        navigate(`/training/campaigns/${response.data.id}`)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create campaign')
    }
  }

  const handleLaunchCampaign = async (campaign: TrainingCampaign) => {
    const confirmed = await confirmDialog({
      title: 'Launch Campaign',
      message: `Are you sure you want to launch "${campaign.name}"? This will create assignments for all targeted users.`,
      confirmText: 'Launch',
      type: 'warning',
    })
    if (confirmed) {
      try {
        const response = await api.launchTrainingCampaign(campaign.id)
        if (response.success) {
          toast.success(`Campaign launched! ${response.assignments_created} assignments created.`)
          fetchCampaigns()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to launch campaign')
      }
    }
  }

  const handleCancelCampaign = async (campaign: TrainingCampaign) => {
    const confirmed = await confirmDialog({
      title: 'Cancel Campaign',
      message: `Are you sure you want to cancel "${campaign.name}"?`,
      confirmText: 'Cancel Campaign',
      type: 'danger',
    })
    if (confirmed) {
      try {
        const response = await api.cancelTrainingCampaign(campaign.id)
        if (response.success) {
          toast.success('Campaign cancelled')
          fetchCampaigns()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to cancel campaign')
      }
    }
  }

  // Category actions
  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        const response = await api.updateTrainingCategory(editingCategory.id, categoryForm)
        if (response.success) {
          toast.success('Category updated')
        }
      } else {
        const response = await api.createTrainingCategory(categoryForm)
        if (response.success) {
          toast.success('Category created')
        }
      }
      setShowCreateCategoryModal(false)
      setEditingCategory(null)
      resetCategoryForm()
      fetchCategories()
    } catch (error: any) {
      toast.error(error.message || 'Failed to save category')
    }
  }

  const handleDeleteCategory = async (category: TrainingCategory) => {
    if (category.is_system) {
      toast.error('Cannot delete system categories')
      return
    }
    const confirmed = await confirmDialog({
      title: 'Delete Category',
      message: `Are you sure you want to delete "${category.name}"? Modules in this category will be uncategorized.`,
      confirmText: 'Delete',
      type: 'danger',
    })
    if (confirmed) {
      try {
        const response = await api.deleteTrainingCategory(category.id)
        if (response.success) {
          toast.success('Category deleted')
          fetchCategories()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete category')
      }
    }
  }

  // Reset forms
  const resetModuleForm = () => {
    setModuleForm({
      title: '',
      description: '',
      category_id: '',
      content_type: 'video',
      video_url: '',
      estimated_minutes: 30,
      is_mandatory: false,
      quiz_passing_score: 70,
    })
  }

  const resetCampaignForm = () => {
    setCampaignForm({
      name: '',
      description: '',
      module_ids: [],
      assignment_type: 'all_users',
      schedule_type: 'immediate',
      due_days_from_assignment: 14,
      send_reminders: true,
      reminder_days_before: [7, 3, 1],
    })
  }

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      icon: 'BookOpen',
      color: '#6366f1',
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Sneak Preview Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-4 text-white shadow-lg">
        <div className="flex items-center gap-3">
          <Sparkles className="h-6 w-6" />
          <div>
            <h2 className="font-semibold text-lg">Sneak Preview</h2>
            <p className="text-purple-100 text-sm">This module is coming soon. The interface below shows planned functionality.</p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance Training</h1>
          <p className="text-gray-500 mt-1">Manage training modules and campaigns</p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'modules' && (
            <button
              onClick={() => setShowCreateModuleModal(true)}
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Module
            </button>
          )}
          {activeTab === 'campaigns' && (
            <button
              onClick={() => setShowCreateCampaignModal(true)}
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </button>
          )}
          {activeTab === 'categories' && (
            <button
              onClick={() => {
                resetCategoryForm()
                setEditingCategory(null)
                setShowCreateCategoryModal(true)
              }}
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Category
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'modules', label: 'Modules', icon: BookOpen },
            { id: 'campaigns', label: 'Campaigns', icon: Send },
            { id: 'categories', label: 'Categories', icon: Folder },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboardStats && (
        <div className="space-y-6">
          {/* Dashboard Sub-Tabs & Export Buttons */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'trends', label: 'Trends', icon: TrendingUp },
                { id: 'performers', label: 'Top Performers', icon: Award },
                { id: 'effectiveness', label: 'Module Effectiveness', icon: Target },
                { id: 'departments', label: 'By Department', icon: Building2 },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setDashboardSubTab(tab.id as DashboardSubTab)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    dashboardSubTab === tab.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAIModal(true)}
                className="btn btn-secondary"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                AI Tools
              </button>
              <div className="relative">
                <button
                  onClick={() => handleExportReport('assignments')}
                  disabled={exportLoading}
                  className="btn btn-secondary"
                >
                  {exportLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export
                </button>
              </div>
            </div>
          </div>

          {/* Stats Cards - Always Visible */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">Total Modules</span>
                <BookOpen className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-2xl font-bold mt-2">{dashboardStats.total_modules}</p>
              <p className="text-xs text-gray-500 mt-1">{dashboardStats.published_modules} published</p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">Active Campaigns</span>
                <Send className="h-5 w-5 text-blue-500" />
              </div>
              <p className="text-2xl font-bold mt-2">{dashboardStats.active_campaigns}</p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">Completion Rate</span>
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-2xl font-bold mt-2">{dashboardStats.overall_completion_rate.toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">Overdue</span>
                <AlertCircle className="h-5 w-5 text-red-500" />
              </div>
              <p className="text-2xl font-bold text-red-600 mt-2">{dashboardStats.overdue_assignments}</p>
            </div>
          </div>

          {/* Loading State for Analytics */}
          {analyticsLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          )}

          {/* Overview Sub-Tab */}
          {dashboardSubTab === 'overview' && !analyticsLoading && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Completion by Category */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Completion by Category</h3>
                {dashboardStats.by_category.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dashboardStats.by_category}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category_name" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                        <Bar dataKey="completion_rate" fill="#6366f1" name="Completion Rate" />
                      </BarChart>
                    </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No category data available
                </div>
              )}
            </div>

            {/* Recent Completions */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Recent Completions</h3>
              {dashboardStats.recent_completions.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {dashboardStats.recent_completions.map((completion, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{completion.user_username}</p>
                        <p className="text-sm text-gray-500">{completion.module_title}</p>
                      </div>
                      <div className="text-right">
                        {completion.score !== null && (
                          <p className="font-medium text-green-600">{completion.score}%</p>
                        )}
                        <p className="text-xs text-gray-400">
                          {new Date(completion.completed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No recent completions
                </div>
              )}
            </div>
          </div>
          )}

          {/* Trends Sub-Tab */}
          {dashboardSubTab === 'trends' && !analyticsLoading && (
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-gray-900">Completion Trends (Last 6 Months)</h3>
                <button
                  onClick={() => fetchTrends()}
                  className="btn btn-sm btn-secondary"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
              {trends.length > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="completions"
                        stroke="#6366f1"
                        strokeWidth={2}
                        name="Completions"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="avg_score"
                        stroke="#22c55e"
                        strokeWidth={2}
                        name="Avg Score %"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-80 flex items-center justify-center text-gray-500">
                  No trend data available
                </div>
              )}
            </div>
          )}

          {/* Top Performers Sub-Tab */}
          {dashboardSubTab === 'performers' && !analyticsLoading && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Top Performers</h3>
                  <button
                    onClick={() => fetchTopPerformers()}
                    className="btn btn-sm btn-secondary"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {topPerformers.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modules Completed</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Score</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quizzes Passed</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topPerformers.map((performer, idx) => (
                      <tr key={performer.user_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                            idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                            idx === 1 ? 'bg-gray-100 text-gray-700' :
                            idx === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                            {idx + 1}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {performer.first_name && performer.last_name
                                ? `${performer.first_name} ${performer.last_name}`
                                : performer.username}
                            </p>
                            <p className="text-sm text-gray-500">{performer.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-lg font-semibold text-primary-600">{performer.modules_completed}</span>
                        </td>
                        <td className="px-6 py-4">
                          {performer.avg_score !== null ? (
                            <span className="text-green-600 font-medium">{performer.avg_score.toFixed(0)}%</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-700">{performer.quizzes_passed}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  No performance data available
                </div>
              )}
            </div>
          )}

          {/* Module Effectiveness Sub-Tab */}
          {dashboardSubTab === 'effectiveness' && !analyticsLoading && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Module Effectiveness</h3>
                  <button
                    onClick={() => fetchModuleEffectiveness()}
                    className="btn btn-sm btn-secondary"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {moduleEffectiveness.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pass Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {moduleEffectiveness.map((module) => (
                      <tr key={module.module_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">{module.title}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-600">{contentTypeLabels[module.content_type]}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-700">{module.total_attempts}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-primary-600 h-2 rounded-full"
                                style={{ width: `${module.completion_rate}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-600">{module.completion_rate}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {module.pass_rate !== null ? (
                            <span className={`font-medium ${module.pass_rate >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                              {module.pass_rate}%
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {module.avg_completion_minutes !== null ? (
                            <span className="text-gray-600">{module.avg_completion_minutes} min</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  No module effectiveness data available
                </div>
              )}
            </div>
          )}

          {/* Departments Sub-Tab */}
          {dashboardSubTab === 'departments' && !analyticsLoading && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Training by Department/Role</h3>
                  <button
                    onClick={() => fetchDepartmentBreakdown()}
                    className="btn btn-sm btn-secondary"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {departmentBreakdown.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                  {departmentBreakdown.map((dept) => (
                    <div key={dept.role} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 capitalize">{dept.role}</h4>
                        <span className="text-sm text-gray-500">{dept.total_assignments} assigned</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Completion Rate</span>
                          <span className={`font-medium ${dept.completion_rate >= 80 ? 'text-green-600' : dept.completion_rate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {dept.completion_rate}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${dept.completion_rate >= 80 ? 'bg-green-500' : dept.completion_rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${dept.completion_rate}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 pt-2">
                          <span>{dept.completed} completed</span>
                          <span className="text-red-500">{dept.overdue} overdue</span>
                        </div>
                        {dept.avg_days_to_complete !== null && (
                          <p className="text-xs text-gray-500">
                            Avg completion: {dept.avg_days_to_complete} days
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  No department data available
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modules Tab */}
      {activeTab === 'modules' && (
        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search modules..."
                  value={moduleSearch}
                  onChange={(e) => setModuleSearch(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn btn-secondary"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
              </button>
            </div>

            {showFilters && (
              <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={moduleStatusFilter}
                    onChange={(e) => setModuleStatusFilter(e.target.value as TrainingModuleStatus | '')}
                    className="input w-full"
                  >
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={moduleCategoryFilter}
                    onChange={(e) => setModuleCategoryFilter(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setModuleSearch('')
                      setModuleStatusFilter('')
                      setModuleCategoryFilter('')
                    }}
                    className="btn btn-secondary"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Modules List */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {modules.map((module) => {
                  const TypeIcon = contentTypeIcons[module.content_type]
                  return (
                    <tr key={module.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <TypeIcon className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <Link
                              to={`/training/modules/${module.id}`}
                              className="font-medium text-gray-900 hover:text-primary-600"
                            >
                              {module.title}
                            </Link>
                            {module.is_mandatory && (
                              <span className="ml-2 text-xs text-red-600">(Mandatory)</span>
                            )}
                            {module.description && (
                              <p className="text-sm text-gray-500 line-clamp-1">{module.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {module.category_name ? (
                          <span
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs"
                            style={{
                              backgroundColor: `${module.category_color || '#6b7280'}20`,
                              color: module.category_color || '#6b7280',
                            }}
                          >
                            {module.category_name}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {contentTypeLabels[module.content_type]}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[module.status]}`}>
                          {module.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {module.estimated_minutes} min
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/training/modules/${module.id}`}
                            className="btn btn-sm btn-secondary"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          {module.status === 'draft' && (
                            <button
                              onClick={() => handlePublishModule(module)}
                              className="btn btn-sm btn-primary"
                              title="Publish"
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          )}
                          {module.status === 'published' && (
                            <button
                              onClick={() => handleArchiveModule(module)}
                              className="btn btn-sm btn-secondary"
                              title="Archive"
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteModule(module)}
                            className="btn btn-sm text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {modules.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No modules found</h3>
                <p className="text-gray-500 mb-4">Get started by creating your first training module.</p>
                <button
                  onClick={() => setShowCreateModuleModal(true)}
                  className="btn btn-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Module
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {modulePagination.total > modulePagination.limit && (
            <div className="flex items-center justify-between bg-white px-4 py-3 border rounded-lg">
              <div className="text-sm text-gray-500">
                Showing {modulePagination.offset + 1} to {Math.min(modulePagination.offset + modules.length, modulePagination.total)} of {modulePagination.total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setModulePagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
                  disabled={modulePagination.offset === 0}
                  className="btn btn-sm btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setModulePagination(p => ({ ...p, offset: p.offset + p.limit }))}
                  disabled={modulePagination.offset + modules.length >= modulePagination.total}
                  className="btn btn-sm btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={campaignSearch}
                  onChange={(e) => setCampaignSearch(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
              <select
                value={campaignStatusFilter}
                onChange={(e) => setCampaignStatusFilter(e.target.value as TrainingCampaignStatus | '')}
                className="input w-48"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Campaigns List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="bg-white rounded-lg border hover:shadow-md transition-shadow">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <Link
                        to={`/training/campaigns/${campaign.id}`}
                        className="font-semibold text-gray-900 hover:text-primary-600"
                      >
                        {campaign.name}
                      </Link>
                      <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[campaign.status]}`}>
                        {campaign.status}
                      </span>
                    </div>
                  </div>

                  {campaign.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">{campaign.description}</p>
                  )}

                  {campaign.stats && (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1 text-gray-600">
                        <Users className="h-4 w-4" />
                        {campaign.stats.total_assignments} assigned
                      </div>
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        {campaign.stats.completed} completed
                      </div>
                      <div className="flex items-center gap-1 text-blue-600">
                        <Clock className="h-4 w-4" />
                        {campaign.stats.in_progress} in progress
                      </div>
                      <div className="flex items-center gap-1 text-red-600">
                        <AlertCircle className="h-4 w-4" />
                        {campaign.stats.overdue} overdue
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t p-3 flex items-center justify-between">
                  <Link
                    to={`/training/campaigns/${campaign.id}`}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    View Details
                  </Link>
                  <div className="flex gap-2">
                    {campaign.status === 'draft' && (
                      <button
                        onClick={() => handleLaunchCampaign(campaign)}
                        className="btn btn-sm btn-primary"
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Launch
                      </button>
                    )}
                    {campaign.status === 'active' && (
                      <button
                        onClick={() => handleCancelCampaign(campaign)}
                        className="btn btn-sm btn-secondary text-red-600"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {campaigns.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border">
              <Send className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
              <p className="text-gray-500 mb-4">Create a campaign to assign training to users.</p>
              <button
                onClick={() => setShowCreateCampaignModal(true)}
                className="btn btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Campaign
              </button>
            </div>
          )}
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div
              key={category.id}
              className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${category.color}20`, color: category.color }}
                  >
                    <Folder className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{category.name}</h3>
                    {category.is_system && (
                      <span className="text-xs text-gray-500">System</span>
                    )}
                  </div>
                </div>
                {!category.is_system && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        setEditingCategory(category)
                        setCategoryForm({
                          name: category.name,
                          description: category.description || '',
                          icon: category.icon,
                          color: category.color,
                        })
                        setShowCreateCategoryModal(true)
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              {category.description && (
                <p className="text-sm text-gray-500 mb-3">{category.description}</p>
              )}
              <div className="text-sm text-gray-600">
                {category.module_count || 0} modules
              </div>
            </div>
          ))}

          {categories.length === 0 && (
            <div className="col-span-full text-center py-12 bg-white rounded-lg border">
              <Folder className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No categories yet</h3>
              <p className="text-gray-500 mb-4">Organize your training modules with categories.</p>
              <button
                onClick={() => {
                  resetCategoryForm()
                  setEditingCategory(null)
                  setShowCreateCategoryModal(true)
                }}
                className="btn btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Category
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Module Modal */}
      {showCreateModuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Create Training Module</h2>
              <button
                onClick={() => {
                  setShowCreateModuleModal(false)
                  resetModuleForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={moduleForm.title}
                  onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., Security Awareness Basics"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={moduleForm.description}
                  onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                  className="input w-full"
                  rows={3}
                  placeholder="What will users learn in this module?"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={moduleForm.category_id}
                    onChange={(e) => setModuleForm({ ...moduleForm, category_id: e.target.value })}
                    className="input w-full"
                  >
                    <option value="">Select Category</option>
                    {categories.filter(c => c.is_active).map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={moduleForm.content_type}
                    onChange={(e) => setModuleForm({ ...moduleForm, content_type: e.target.value as TrainingContentType })}
                    className="input w-full"
                  >
                    {Object.entries(contentTypeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {moduleForm.content_type === 'video' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
                  <input
                    type="url"
                    value={moduleForm.video_url}
                    onChange={(e) => setModuleForm({ ...moduleForm, video_url: e.target.value })}
                    className="input w-full"
                    placeholder="https://youtube.com/watch?v=..."
                  />
                  <p className="text-xs text-gray-500 mt-1">YouTube, Vimeo, or direct video URL</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    value={moduleForm.estimated_minutes}
                    onChange={(e) => setModuleForm({ ...moduleForm, estimated_minutes: parseInt(e.target.value) || 0 })}
                    className="input w-full"
                    min="1"
                  />
                </div>

                {(moduleForm.content_type === 'quiz' || moduleForm.content_type === 'mixed') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Passing Score (%)</label>
                    <input
                      type="number"
                      value={moduleForm.quiz_passing_score}
                      onChange={(e) => setModuleForm({ ...moduleForm, quiz_passing_score: parseInt(e.target.value) || 70 })}
                      className="input w-full"
                      min="0"
                      max="100"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={moduleForm.is_mandatory}
                    onChange={(e) => setModuleForm({ ...moduleForm, is_mandatory: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Mandatory training</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">Mandatory modules are required for compliance.</p>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModuleModal(false)
                  resetModuleForm()
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateModule}
                disabled={!moduleForm.title}
                className="btn btn-primary disabled:opacity-50"
              >
                Create Module
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateCampaignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Create Training Campaign</h2>
              <button
                onClick={() => {
                  setShowCreateCampaignModal(false)
                  resetCampaignForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., Q1 2025 Security Training"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                  className="input w-full"
                  rows={2}
                  placeholder="Campaign objectives..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Modules <span className="text-red-500">*</span>
                </label>
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {modules.filter(m => m.status === 'published').map((module) => (
                    <label
                      key={module.id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={campaignForm.module_ids.includes(module.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCampaignForm({ ...campaignForm, module_ids: [...campaignForm.module_ids, module.id] })
                          } else {
                            setCampaignForm({ ...campaignForm, module_ids: campaignForm.module_ids.filter(id => id !== module.id) })
                          }
                        }}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{module.title}</p>
                        <p className="text-xs text-gray-500">{module.estimated_minutes} min</p>
                      </div>
                    </label>
                  ))}
                  {modules.filter(m => m.status === 'published').length === 0 && (
                    <div className="p-4 text-center text-gray-500">
                      No published modules available
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                <select
                  value={campaignForm.assignment_type}
                  onChange={(e) => setCampaignForm({ ...campaignForm, assignment_type: e.target.value as TrainingAssignmentType })}
                  className="input w-full"
                >
                  <option value="all_users">All Users</option>
                  <option value="specific_users">Specific Users</option>
                  <option value="by_role">By Role</option>
                  <option value="by_department">By Department</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={campaignForm.due_days_from_assignment}
                    onChange={(e) => setCampaignForm({ ...campaignForm, due_days_from_assignment: parseInt(e.target.value) || 14 })}
                    className="input w-24"
                    min="1"
                  />
                  <span className="text-gray-600">days after assignment</span>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={campaignForm.send_reminders}
                    onChange={(e) => setCampaignForm({ ...campaignForm, send_reminders: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Send reminder emails</span>
                </label>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateCampaignModal(false)
                  resetCampaignForm()
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCampaign}
                disabled={!campaignForm.name || campaignForm.module_ids.length === 0}
                className="btn btn-primary disabled:opacity-50"
              >
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Category Modal */}
      {showCreateCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingCategory ? 'Edit Category' : 'Create Category'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateCategoryModal(false)
                  setEditingCategory(null)
                  resetCategoryForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., Security"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  className="input w-full"
                  rows={2}
                  placeholder="Category description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                    className="h-10 w-10 rounded border cursor-pointer"
                  />
                  <input
                    type="text"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                    className="input flex-1"
                    placeholder="#6366f1"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateCategoryModal(false)
                  setEditingCategory(null)
                  resetCategoryForm()
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={!categoryForm.name}
                className="btn btn-primary disabled:opacity-50"
              >
                {editingCategory ? 'Save Changes' : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Tools Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">AI Training Tools</h2>
                  <p className="text-sm text-gray-500">Generate content using AI</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAIModal(false)
                  setGeneratedQuestions([])
                  setAiQuizTopic('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Quiz Generator */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-primary-600" />
                  Quiz Question Generator
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
                    <input
                      type="text"
                      value={aiQuizTopic}
                      onChange={(e) => setAiQuizTopic(e.target.value)}
                      className="input w-full"
                      placeholder="e.g., Phishing awareness, Password security, Data protection"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                      <select
                        value={aiQuizDifficulty}
                        onChange={(e) => setAiQuizDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
                        className="input w-full"
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Number of Questions</label>
                      <select
                        value={aiNumQuestions}
                        onChange={(e) => setAiNumQuestions(parseInt(e.target.value))}
                        className="input w-full"
                      >
                        <option value="3">3 questions</option>
                        <option value="5">5 questions</option>
                        <option value="10">10 questions</option>
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={handleGenerateQuiz}
                    disabled={aiGenerating || !aiQuizTopic.trim()}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {aiGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Questions
                      </>
                    )}
                  </button>
                </div>

                {/* Generated Questions Preview */}
                {generatedQuestions.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Generated Questions ({generatedQuestions.length})</h4>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {generatedQuestions.map((q, idx) => (
                        <div key={q.id} className="bg-gray-50 rounded-lg p-3">
                          <p className="font-medium text-gray-900 text-sm mb-2">
                            {idx + 1}. {q.question_text}
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {q.options.map((opt: any) => (
                              <div
                                key={opt.id}
                                className={`text-xs p-2 rounded ${
                                  opt.is_correct
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-white text-gray-600 border'
                                }`}
                              >
                                {opt.text}
                              </div>
                            ))}
                          </div>
                          {q.explanation && (
                            <p className="text-xs text-gray-500 mt-2 italic">{q.explanation}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      These questions can be used in a quiz module. Copy and paste them into the module editor.
                    </p>
                  </div>
                )}
              </div>

              {/* Description Generator */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary-600" />
                  Module Description Generator
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Use AI to generate a professional description for your training module.
                  Enter a title in the module creation form first.
                </p>
                <button
                  onClick={handleGenerateDescription}
                  disabled={aiGenerating || !moduleForm.title.trim()}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  {aiGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Description
                    </>
                  )}
                </button>
                {!moduleForm.title.trim() && (
                  <p className="text-xs text-gray-400 mt-2">
                    Enter a module title in the create form to enable this feature
                  </p>
                )}
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex items-center justify-end">
              <button
                onClick={() => {
                  setShowAIModal(false)
                  setGeneratedQuestions([])
                  setAiQuizTopic('')
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
